/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Flex,
  Pagination,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { Divider, Select } from 'antd';
import { Link } from 'react-router-dom';

import { ObjectDetailPage, SecondaryText } from '../../../common/components';
import FilterDrawer from '../../../common/components/FilterDrawer/FilterDrawer';
import { CloseIcon } from '../../../common/Icons';
import CityTreeSelect from '../../../common/selects/CityTreeSelect';
import CountrySelect from '../../../common/selects/CountrySelect';
import VehicleTreeSelect from '../../../common/selects/VehicleTreeSelect';
import Service, { Rollout } from '../../../lib/App';
import { VARIABLE_PAGE_SIZE } from '../../../store/state/variables/actions';
import { sendScribeEvent } from '../../../utils/scribe';
import Search from './Search';
import VariablesSearchRecords from './VariablesSearchRecords';
import { searchByConstraintsEvents } from '@/common/Scribe/EventsList';
import { getConfigurationsFilters } from '@/pages/Product/Configurations/const';

export interface SearchByConstraintsItem {
  description?: string;
  name?: string;
  rollouts?: Rollout;
  type?: string;
  productEntity?: string;
}

interface IVariablesSearchProps {
  hideTitle?: boolean;
  hideSearch?: boolean;
  hideFilter?: boolean;
  data?: SearchByConstraintsItem;
  hidePadding?: boolean;
}

export const enum FacetType {
  COUNTRY = 'ctry',
  CITY = 'city',
  VECHILE_TYPE_ID = 'svc',
}

const COLINDEX_DISABLED = 0;
const MAX_COLUMNS = 8;

export const ConstraintFacetsOptions = [
  {
    value: FacetType.COUNTRY,
    label: 'Country',
  },
  {
    value: FacetType.CITY,
    label: 'City',
  },
  {
    value: FacetType.VECHILE_TYPE_ID,
    label: 'Vehicle Type',
  },
];

type VariableSearchColumnConstraint = {
  value: string;
  facet: FacetType;
};

export type VariableSearchColumn = {
  constraint: VariableSearchColumnConstraint;
};

const defaultColumn = {
  constraint: {
    facet: FacetType.COUNTRY,
    value: '',
  },
} as VariableSearchColumn;

const defaultLeftRightPadding = '32px';

export const NON_COLUMN_INDEX = -1;

export const generateScribeEventData = (
  columnIndex: number,
  columns: VariableSearchColumn[],
  filters: { [key: string]: any },
  pageNumber: number,
  total: number,
  searchVal: string,
) => {
  const facets_array = {};
  const values_array = {};

  columns.forEach((column: VariableSearchColumn, index: number) => {
    const { facet, value } = column.constraint;

    facets_array[index] = facet;
    values_array[index] = value;
  });

  const data = {
    facets_array,
    values_array,
    Search_box_text: searchVal,
    Filters_selected: filters,
    Results_returned: total,
    Page_number: pageNumber,
  };

  if (columnIndex > 0) data['Search_number'] = columnIndex;

  return data;
};

const VariablesSearch: FC<IVariablesSearchProps> = ({
  hideTitle,
  hideSearch,
  hideFilter,
  data,
  hidePadding,
}) => {
  const [searchVal, setSearchVal] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [columns, updateColumns] = useState([defaultColumn]);
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [loadingColIndex, setLoadingColIndex] = useState(-1);
  const variableSearchCardRef = useRef(null);

  const addColumn = () => {
    const columnsToUpdate = columns.slice();

    if (columns?.length < MAX_COLUMNS) {
      columnsToUpdate.push({ ...defaultColumn });

      updateColumns(columnsToUpdate);

      sendScribeEvent(searchByConstraintsEvents.addSearch.name, {
        STATE_NAME: searchByConstraintsEvents.addSearch.state,
        ...generateScribeEventData(
          NON_COLUMN_INDEX,
          columnsToUpdate,
          filters,
          pageNumber,
          total,
          searchVal,
        ),
      });
    }
  };

  const deleteColumn = (index: number) => {
    const columnsToUpdate = columns.slice();

    columnsToUpdate.splice(index, 1);

    sendScribeEvent(searchByConstraintsEvents.removeSearch.name, {
      STATE_NAME: searchByConstraintsEvents.removeSearch.state,
      ...generateScribeEventData(
        index + 1,
        columnsToUpdate,
        filters,
        pageNumber,
        total,
        searchVal,
      ),
    });

    updateColumns(columnsToUpdate);
  };

  const onFiltersApply = (filters: { [key: string]: any }) => {
    setFilters(filters);
    setPageNumber(1);

    constraintsSearch(null, searchVal, filters, 1, 'filter', columns);
  };

  const constraintsSearch = async (
    facetValue: string,
    searchVal: string,
    filters: { [key: string]: any },
    pageNumber: number,
    action?: string,
    columns?: VariableSearchColumn[],
  ) => {
    setIsLoading(true);

    const sendInstrumentation = (total) => {
      switch (action) {
        case 'filter':
          sendScribeEvent(searchByConstraintsEvents.removeSearch.name, {
            STATE_NAME: searchByConstraintsEvents.removeSearch.state,
            ...generateScribeEventData(
              NON_COLUMN_INDEX,
              columns,
              filters,
              pageNumber,
              total,
              searchVal,
            ),
          });
          break;
        case 'search':
          sendScribeEvent(searchByConstraintsEvents.useSearchBox.name, {
            STATE_NAME: searchByConstraintsEvents.useSearchBox.state,
            ...generateScribeEventData(
              NON_COLUMN_INDEX,
              columns,
              filters,
              pageNumber,
              total,
              searchVal,
            ),
          });
          break;
        case 'queryRecords':
          sendScribeEvent(searchByConstraintsEvents.selectValue.name, {
            STATE_NAME: searchByConstraintsEvents.selectFacet.state,
            ...generateScribeEventData(
              1,
              columns,
              filters,
              pageNumber,
              total,
              searchVal,
            ),
          });
          break;
        default:
          break;
      }
    };

    try {
      const filtersCopy = { ...filters };

      if (data) {
        if (!data?.productEntity) searchVal = data?.name;
        else filtersCopy['productEntity'] = data.productEntity;
      }

      const res = await Service.planner.constraintsSearch({
        facet_type: columns?.[0]?.constraint?.facet,
        facet_value: facetValue || columns?.[0]?.constraint?.value,
        search_term: searchVal,
        limit: VARIABLE_PAGE_SIZE,
        offset: (pageNumber - 1) * VARIABLE_PAGE_SIZE,
        variable_filters: filtersCopy,
      });

      const total =
        data?.name && !data?.productEntity ? 1 : res?.data?.total || 0;
      const records: Array<SearchByConstraintsItem> = res?.data?.items || [];

      if (data?.name && !data?.productEntity) {
        const namedRecord = records.find((record) => record.name === data.name);

        setRecords([namedRecord]);
      } else {
        setRecords(res?.data?.items || []);
      }

      sendInstrumentation(total);
      setTotal(total);

      setIsLoading(false);

      variableSearchCardRef.current.children[0].scrollTo(0, 0);
    } catch (e) {
      setRecords([]);
      setPageNumber(1);
      setTotal(0);

      sendInstrumentation(0);

      setIsLoading(false);
    }
  };

  // this will overwrite the elements width to the correct value when the elements width are set by useEffect columns on first page rendering
  const resizeObserver = new ResizeObserver((entries) => {
    if (
      entries?.[0].target === document.getElementById('variables-search-tbl')
    ) {
      if (document.getElementById('variables-search-tbl'))
        document.getElementById('variables-search-tbl').style.width =
          `calc(${variableSearchCardRef.current.offsetWidth}px - 17px)`;

      if (document.getElementsByClassName('variables-search-alert').length > 0)
        document
          .getElementsByClassName('variables-search-alert')[0]
          .setAttribute(
            'width',
            `calc(${variableSearchCardRef.current.offsetWidth}px - 18px - ${defaultLeftRightPadding})`,
          );

      if (document.getElementById('variables-search-search'))
        document.getElementById('variables-search-search').style.width =
          `calc(${variableSearchCardRef.current.offsetWidth}px - 18px - ${defaultLeftRightPadding})`;

      if (document.getElementById('variables-search-pagination'))
        document.getElementById('variables-search-pagination').style.width =
          `calc(${variableSearchCardRef.current.offsetWidth}px - 18px - ${defaultLeftRightPadding})`;
    }
  });

  useEffect(() => {
    sendScribeEvent(searchByConstraintsEvents.pageView.name, {
      STATE_NAME: searchByConstraintsEvents.pageView.state,
    });

    resizeObserver.observe(document.getElementById('variables-search-tbl'));
  }, []);

  const renderColumnTitle = () => {
    if (!records?.length && !columns[0].constraint.value) return null;

    const items = [
      <th key={'column-title-cell-0'} className="column-title">
        Variable Name
      </th>,
      ...columns.map((_column, colIndex: number) => (
        <th key={`column-title-cell-${colIndex + 1}`} className="column-title">
          Returned Value
        </th>
      )),
    ];

    return (
      <tr
        className={`variables-search-column-title ${
          hideFilter && hideSearch
            ? 'variables-search-column-title-without-search-and-filter'
            : ''
        } `}
      >
        {items}
      </tr>
    );
  };

  const updateColumnFacet = (facet: string, index: number): void => {
    const columnsClone = JSON.parse(JSON.stringify(columns));

    columnsClone[index].constraint.facet = facet;
    columnsClone[index].constraint.value = '';

    updateColumns(columnsClone);

    if (index === 0) {
      setRecords([]);

      sendScribeEvent(searchByConstraintsEvents.selectFacet.name, {
        STATE_NAME: searchByConstraintsEvents.selectFacet.state,
        ...generateScribeEventData(
          index + 1,
          columnsClone,
          filters,
          pageNumber,
          0,
          searchVal,
        ),
      });

      return;
    }

    sendScribeEvent(searchByConstraintsEvents.selectFacet.name, {
      STATE_NAME: searchByConstraintsEvents.selectFacet.state,
      ...generateScribeEventData(
        index + 1,
        columnsClone,
        filters,
        pageNumber,
        total,
        searchVal,
      ),
    });
  };

  const updateColumnValue = (value: any, index: number): void => {
    const columnsClone = JSON.parse(JSON.stringify(columns));

    columnsClone[index].constraint.value = value;

    updateColumns(columnsClone);

    if (index === 0)
      constraintsSearch(
        value,
        searchVal,
        filters,
        1,
        'queryRecords',
        columnsClone,
      );
    else {
      sendScribeEvent(searchByConstraintsEvents.selectValue.name, {
        STATE_NAME: searchByConstraintsEvents.selectFacet.state,
        ...generateScribeEventData(
          index + 1,
          columnsClone,
          filters,
          pageNumber,
          total,
          searchVal,
        ),
      });

      setLoadingColIndex(index);

      setTimeout(() => {
        setLoadingColIndex(-1);
      }, 1000);
    }
  };

  const renderColGroup = () => {
    const items = [
      <col
        key="variables-search-colgroup-0"
        style={{
          width: '324px',
        }}
      />,
      ...columns.map((_column, index) => (
        <col
          key={`variables-search-colgroup-${index + 1}`}
          style={{
            minWidth: '484px',
            width: `calc(100% / ${columns.length})`,
          }}
        />
      )),
    ];

    return items;
  };

  const renderHeader = () => {
    const items = [
      <th
        key={'header-cell-0'}
        style={{
          padding: '8px 16px',
        }}
      />,
      columns.map((_column, index) => (
        <th
          key={`header-cell-${index + 1}`}
          style={{
            padding: '8px 16px',
          }}
        >
          <div className="header-cell">
            {`Search #${index + 1}`}
            <div className="flex-row-center-aligned">
              {index > 0 && <CloseIcon onClick={() => deleteColumn(index)} />}
              {index > 0 &&
                index === columns.length - 1 &&
                columns?.length < MAX_COLUMNS && <Divider type="vertical" />}
              {index === columns.length - 1 &&
                columns?.length < MAX_COLUMNS && (
                  <div
                    style={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      size="small"
                      disabled={!records?.length}
                      onClick={addColumn}
                    >
                      Add Search
                    </Button>
                  </div>
                )}
            </div>
          </div>
        </th>
      )),
    ];

    return <tr>{items}</tr>;
  };

  const renderConstraint = () => {
    const items = [
      <th key={'constraints-cell-0'} className="constraint-cell" />,
      ...columns.map((_column, colIndex: number) => (
        <th
          key={`constraints-cell-${colIndex + 1}`}
          className="constraint-cell"
        >
          <div className="flex-row-center-aligned half-gap w100">
            <div className="field-container flex-one-one">
              <SecondaryText ellipsis>Select Facet</SecondaryText>
              <Select
                disabled={columns?.length > 1 && colIndex === COLINDEX_DISABLED}
                value={columns[colIndex]?.constraint?.facet}
                className="grab-x__variables-search-select"
                options={ConstraintFacetsOptions}
                onChange={(facet) => updateColumnFacet(facet, colIndex)}
              />
            </div>
            <div className="field-container flex-one-one">
              <SecondaryText>Select Value</SecondaryText>
              {columns[colIndex].constraint.facet === FacetType.COUNTRY && (
                <CountrySelect
                  disabled={
                    columns?.length > 1 && colIndex === COLINDEX_DISABLED
                  }
                  value={columns[colIndex]?.constraint?.value}
                  onChange={(value) => updateColumnValue(value, colIndex)}
                />
              )}
              {columns[colIndex].constraint.facet === FacetType.CITY && (
                <CityTreeSelect
                  disabled={
                    columns?.length > 1 && colIndex === COLINDEX_DISABLED
                  }
                  value={columns[colIndex]?.constraint?.value}
                  onChange={(value) => updateColumnValue(value, colIndex)}
                />
              )}
              {columns[colIndex]?.constraint.facet ===
                FacetType.VECHILE_TYPE_ID && (
                <VehicleTreeSelect
                  disabled={
                    columns?.length > 1 && colIndex === COLINDEX_DISABLED
                  }
                  value={columns[colIndex]?.constraint?.value}
                  onChange={(value) => updateColumnValue(value, colIndex)}
                />
              )}
            </div>
          </div>
        </th>
      )),
    ];

    return <tr>{items}</tr>;
  };

  const renderInfo = () => {
    if (!records?.length && !columns[0].constraint.value) return null;

    return (
      <tr>
        <th colSpan={columns.length + 1} className="alert-cell">
          <Alert
            className="variables-search-alert"
            style={{
              position: 'sticky',
              left: '16px',
              border: 0,
            }}
            type="info"
            message={
              <div
                style={{
                  lineHeight: '20px',
                }}
              >
                Results will include rollout targets and experiments with cities
                and vehicle types within the selected country.
              </div>
            }
          />
        </th>
      </tr>
    );
  };

  function renderProductEntityInfo(): ReactNode {
    if (!data?.productEntity) return false;

    return (
      <Tooltip
        title={`You are currently viewing variables under ${data.productEntity}`}
      >
        <Typography.Text strong>
          ({`You are currently viewing variables under ${data.productEntity}`})
        </Typography.Text>
      </Tooltip>
    );
  }

  function renderSearch(): ReactNode {
    if (hideSearch) return false;

    return (
      <Flex align="center" gap={8}>
        <Search
          onSearch={(searchVal) => {
            setPageNumber(1);
            setSearchVal(searchVal);

            constraintsSearch(null, searchVal, filters, 1, 'search', columns);
          }}
        />
        {renderProductEntityInfo()}
      </Flex>
    );
  }

  function renderFilter(): ReactNode {
    if (hideFilter) return false;

    return (
      <FilterDrawer
        filters={filters}
        groups={getConfigurationsFilters()}
        onApply={onFiltersApply}
      />
    );
  }

  function renderSearchGroup(): ReactNode {
    if (!records?.length && !columns[0].constraint.value) return false;

    if (hideFilter && hideSearch) return false;

    return (
      <tr>
        <th
          colSpan={columns.length + 1}
          className="search-cell"
          style={{
            padding: '8px 16px',
          }}
        >
          <div
            id="variables-search-search"
            className="header-cell"
            style={{
              position: 'sticky',
              left: '16px',
            }}
          >
            {renderSearch()}
            {renderFilter()}
          </div>
        </th>
      </tr>
    );
  }

  const renderFooter = () => {
    if (!records?.length || isLoading) return null;

    return (
      <tr>
        <td colSpan={columns.length + 1}>
          <div
            id="variables-search-pagination"
            style={{
              position: 'sticky',
              left: '16px',
              textAlign: 'end',
            }}
          >
            <Pagination
              showSizeChanger={false}
              total={total}
              pageSize={VARIABLE_PAGE_SIZE}
              current={pageNumber}
              onChange={(pageNumber) => {
                sendScribeEvent(
                  searchByConstraintsEvents.clickPageNumber.name,
                  {
                    STATE_NAME: searchByConstraintsEvents.clickPageNumber.state,
                    ...generateScribeEventData(
                      NON_COLUMN_INDEX,
                      columns,
                      filters,
                      pageNumber,
                      total,
                      searchVal,
                    ),
                  },
                );

                setPageNumber(pageNumber);

                constraintsSearch(
                  null,
                  searchVal,
                  filters,
                  pageNumber,
                  null,
                  columns,
                );
              }}
              showTotal={(total: number, range: [number, number]) => {
                return <>{`${range?.[0]} - ${range?.[1]} of ${total}`}</>;
              }}
            />
          </div>
        </td>
      </tr>
    );
  };

  function renderTitle(): ReactNode {
    if (hideTitle) return false;

    return (
      <Typography.Title level={3}>
        <Link to="../variables">Variables</Link> / Search by Constraints
      </Typography.Title>
    );
  }

  return (
    <ObjectDetailPage
      id="variables-search"
      style={{
        padding: hidePadding ? 0 : 24,
      }}
    >
      {renderTitle()}
      <div ref={variableSearchCardRef} className="variables-search-card">
        <Card
          id="variables-search-card-wrapper"
          className="h100"
          styles={{
            body: {
              padding: 0,
              height: '100%',
              overflow: 'auto',
              borderRadius: 14,
            },
          }}
        >
          <table id="variables-search-tbl">
            <colgroup>{renderColGroup()}</colgroup>
            <thead>
              {renderHeader()}
              {renderConstraint()}
              {renderInfo()}
              {renderSearchGroup()}
              {renderColumnTitle()}
            </thead>
            <VariablesSearchRecords
              records={records}
              isLoading={isLoading}
              loadingColIndex={loadingColIndex}
              columns={columns}
              searchVal={searchVal}
              filters={filters}
              pageNumber={pageNumber}
              total={total}
              hideFilter={hideFilter}
              hideSearch={hideSearch}
            />
            <tfoot>{renderFooter()}</tfoot>
          </table>
        </Card>
      </div>
    </ObjectDetailPage>
  );
};

export default VariablesSearch;
